/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, ListGroup } from 'react-bootstrap';
import './SearchableSelect.css';

// Função para obter o texto de exibição
const getDisplayText = (option, displayKey) => {
  if (typeof displayKey === 'function') {
    return displayKey(option);
  }
  if (Array.isArray(displayKey)) {
    return displayKey
      .map((key) => option[key])
      .filter(Boolean)
      .join(' - ');
  }
  return option[displayKey] || '';
};

function SearchableSelect({
  options = [],
  value,
  onChange,
  onInputChange,
  placeholder = 'Digite para pesquisar...',
  displayKey = 'name',
  valueKey = 'id',
  disabled = false,
  required = false,
  className = '',
  maxHeight = '200px',
  allowCreate = false,
  onCreateOption = undefined,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Encontrar a opção selecionada baseada no value
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find(
        (option) => String(option[valueKey]) === String(value)
      );
      setSelectedOption(selected);
      if (selected) {
        setSearchTerm(getDisplayText(selected, displayKey));
      }
    } else {
      setSelectedOption(null);
      if (typeof value === 'string') {
        setSearchTerm(value);
      } else {
        setSearchTerm('');
      }
    }
  }, [value, options, valueKey, displayKey]);

  // Filtrar opções baseado no termo de busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) => {
        const displayText = getDisplayText(option, displayKey);
        return displayText.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayKey]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        // Restaurar o texto da opção selecionada se não houver seleção válida
        if (selectedOption) {
          setSearchTerm(getDisplayText(selectedOption, displayKey));
        } else if (typeof value === 'string' && value.trim()) {
          setSearchTerm(value);
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedOption, displayKey]);

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsOpen(true);
    if (typeof onInputChange === 'function') {
      onInputChange(newSearchTerm);
    }

    // Se o campo estiver vazio, limpar a seleção
    if (!newSearchTerm.trim()) {
      onChange('');
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm(getDisplayText(option, displayKey));
    setIsOpen(false);
    onChange(option[valueKey]);
    if (inputRef.current) inputRef.current.blur();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      if (inputRef.current) inputRef.current.blur();
    }
  };

  return (
    <div className={`searchable-select ${className}`} ref={containerRef}>
      <Form.Control
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
      />

      {isOpen && filteredOptions.length > 0 && (
        <div className="searchable-select-dropdown">
          <ListGroup
            style={{
              maxHeight,
              overflowY: 'auto',
              border: '1px solid #ced4da',
              borderRadius: '0.375rem',
              boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
            }}
          >
            {filteredOptions.map((option, index) => (
              <ListGroup.Item
                key={`${option[valueKey]}-${index}`}
                action
                onClick={() => handleOptionSelect(option)}
                className={
                  selectedOption &&
                  selectedOption[valueKey] === option[valueKey]
                    ? 'active'
                    : ''
                }
                style={{ cursor: 'pointer', padding: '0.5rem 0.75rem' }}
              >
                {getDisplayText(option, displayKey)}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      {isOpen && filteredOptions.length === 0 && searchTerm.trim() && (
        <div className="searchable-select-dropdown">
          <ListGroup>
            {allowCreate ? (
              <ListGroup.Item
                action
                onClick={() => {
                  const newVal = searchTerm.trim();
                  setIsOpen(false);
                  onChange(newVal);
                  if (typeof onCreateOption === 'function')
                    onCreateOption(newVal);
                }}
                style={{
                  cursor: 'pointer',
                  padding: '0.5rem 0.75rem',
                  color: '#0d6efd',
                }}
              >
                + Criar &ldquo;{searchTerm}&rdquo;
              </ListGroup.Item>
            ) : (
              <ListGroup.Item disabled style={{ padding: '0.5rem 0.75rem' }}>
                Nenhum resultado encontrado
              </ListGroup.Item>
            )}
          </ListGroup>
        </div>
      )}
    </div>
  );
}

SearchableSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  placeholder: PropTypes.string,
  displayKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  valueKey: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  maxHeight: PropTypes.string,
  allowCreate: PropTypes.bool,
  onCreateOption: PropTypes.func,
};

SearchableSelect.defaultProps = {
  options: [],
  value: '',
  placeholder: 'Digite para pesquisar...',
  displayKey: 'name',
  valueKey: 'id',
  disabled: false,
  required: false,
  className: '',
  maxHeight: '200px',
  onInputChange: undefined,
  allowCreate: false,
  onCreateOption: undefined,
};

export default SearchableSelect;
